import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import Navbar from '../components/Navbar';
import MSFLogo from '../components/MSFLogo';
import PageBackground from '../components/PageBackground';
import XlsxPreviewModal from '../components/XlsxPreviewModal';
import { Trophy, Car, Bike, FileText, Download, Calendar, ChevronDown, ChevronUp, Table, Flag, Eye } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function RaceResults() {
  const [user, setUser] = useState(null);
  const [raceResults, setRaceResults] = useState([]);
  const [champResults, setChampResults] = useState([]);
  const [activeLeague, setActiveLeague] = useState('racing');
  const [activeSection, setActiveSection] = useState('race'); // 'race' or 'championship'
  const [expandedResult, setExpandedResult] = useState(null);
  const [loading, setLoading] = useState(true);
  const [previewXlsx, setPreviewXlsx] = useState(null);

  useEffect(() => {
    fetchData();
  }, [activeLeague]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [userRes, raceRes, champRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/auth/me`, { withCredentials: true }).catch(() => ({ data: null })),
        axios.get(`${BACKEND_URL}/api/published-results?league=${activeLeague}`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/championship-results?league=${activeLeague}`, { withCredentials: true })
      ]);

      setUser(userRes.data);
      setRaceResults(raceRes.data);
      setChampResults(champRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load results');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
    } catch {
      return dateStr;
    }
  };

  const getPdfs = (result) => {
    if (result.pdf_files && result.pdf_files.length > 0) return result.pdf_files;
    if (result.pdf_url) return [{ name: 'Results PDF', url: result.pdf_url }];
    return [];
  };

  const accentColor = activeLeague === 'racing' ? 'primary' : 'secondary';

  return (
    <PageBackground pageKey="results">
    <div className="min-h-screen">
      <Navbar user={user} />

      <div className="max-w-7xl mx-auto px-4 py-12">
        <div className="mb-8">
          <h1 className="font-heading font-black text-5xl mb-2" data-testid="race-results-title">
            <span className={`text-${accentColor}`}>RESULTS</span> CENTER
          </h1>
          <p className="text-zinc-400">View official race results and championship standings</p>
        </div>

        {/* League Tabs */}
        <div className="flex flex-wrap gap-2 mb-6">
          <button
            onClick={() => setActiveLeague('racing')}
            data-testid="results-racing-tab"
            className={`flex items-center space-x-2 px-6 py-3 font-heading font-bold uppercase transition-all ${
              activeLeague === 'racing'
                ? 'bg-primary text-white'
                : 'bg-zinc-800 text-zinc-400 hover:bg-zinc-700'
            }`}
          >
            <Car size={20} />
            <MSFLogo suffix="Racing" size="sm" />
          </button>
          <button
            onClick={() => setActiveLeague('moto')}
            data-testid="results-moto-tab"
            className={`flex items-center space-x-2 px-6 py-3 font-heading font-bold uppercase transition-all ${
              activeLeague === 'moto'
                ? 'bg-secondary text-black'
                : 'bg-zinc-800 text-zinc-400 hover:bg-zinc-700'
            }`}
          >
            <Bike size={20} />
            <MSFLogo suffix="Moto" size="sm" />
          </button>
        </div>

        {/* Section Tabs */}
        <div className="flex gap-4 mb-8 border-b border-zinc-800">
          <button
            onClick={() => setActiveSection('race')}
            data-testid="section-race-tab"
            className={`flex items-center space-x-2 px-4 py-3 font-bold transition-all border-b-2 -mb-[1px] ${
              activeSection === 'race'
                ? `border-${accentColor} text-${accentColor}`
                : 'border-transparent text-zinc-400 hover:text-zinc-300'
            }`}
          >
            <Flag size={18} />
            <span>Race Results</span>
            <span className="ml-2 px-2 py-0.5 text-xs bg-zinc-800 rounded">{raceResults.length}</span>
          </button>
          <button
            onClick={() => setActiveSection('championship')}
            data-testid="section-championship-tab"
            className={`flex items-center space-x-2 px-4 py-3 font-bold transition-all border-b-2 -mb-[1px] ${
              activeSection === 'championship'
                ? 'border-green-500 text-green-500'
                : 'border-transparent text-zinc-400 hover:text-zinc-300'
            }`}
          >
            <Trophy size={18} />
            <span>Championship Standings</span>
            <span className="ml-2 px-2 py-0.5 text-xs bg-zinc-800 rounded">{champResults.length}</span>
          </button>
        </div>

        {/* Content */}
        {loading ? (
          <div className="bg-card border border-zinc-800 p-12 text-center">
            <div className="animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full mx-auto mb-4"></div>
            <p className="text-zinc-400">Loading results...</p>
          </div>
        ) : activeSection === 'race' ? (
          /* Race Results Section */
          raceResults.length === 0 ? (
            <div className="bg-card border border-zinc-800 p-12 text-center" data-testid="no-race-results">
              <Flag className="w-16 h-16 text-zinc-600 mx-auto mb-4" />
              <p className="text-zinc-400 text-xl">No race results published yet</p>
              <p className="text-zinc-500 mt-2">Check back after the next race!</p>
            </div>
          ) : (
            <div className="space-y-4">
              {raceResults.map((result) => {
                const pdfs = getPdfs(result);
                
                return (
                  <div key={result.result_id} className="bg-card border border-zinc-800 overflow-hidden" data-testid={`race-result-${result.result_id}`}>
                    <div
                      className={`p-6 cursor-pointer hover:bg-zinc-900 transition-colors ${expandedResult === result.result_id ? 'bg-zinc-900' : ''}`}
                      onClick={() => setExpandedResult(expandedResult === result.result_id ? null : result.result_id)}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-4">
                          <div className={`w-16 h-16 bg-${accentColor} flex items-center justify-center`}>
                            <span className="font-heading font-black text-2xl text-white">R{result.round_number}</span>
                          </div>
                          <div>
                            <h3 className="font-heading font-bold text-xl text-white">{result.race_name}</h3>
                            <div className="flex items-center space-x-3 text-zinc-400 text-sm mt-1">
                              <span className="flex items-center">
                                <Calendar size={14} className="mr-1" />
                                {formatDate(result.race_date)}
                              </span>
                              {pdfs.length > 0 && (
                                <span className="px-2 py-0.5 text-xs font-bold bg-zinc-700 text-zinc-300">
                                  {pdfs.length} PDF{pdfs.length !== 1 ? 's' : ''}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center space-x-4">
                          {expandedResult === result.result_id ? <ChevronUp size={24} className="text-zinc-400" /> : <ChevronDown size={24} className="text-zinc-400" />}
                        </div>
                      </div>
                    </div>

                    {expandedResult === result.result_id && (
                      <div className="border-t border-zinc-800 p-6 bg-zinc-950">
                        {result.description && <p className="text-zinc-300 whitespace-pre-wrap mb-6">{result.description}</p>}
                        
                        {pdfs.length > 0 ? (
                          <div>
                            <h4 className="text-sm font-medium text-zinc-500 mb-3">Results Documents:</h4>
                            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                              {pdfs.map((pdf) => (
                                <a
                                  key={pdf.url}
                                  href={`${BACKEND_URL}${pdf.url}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className={`flex items-center space-x-3 p-4 bg-zinc-900 border border-zinc-800 hover:border-${accentColor} transition-colors`}
                                >
                                  <FileText size={24} className={`text-${accentColor}`} />
                                  <div className="flex-1 min-w-0">
                                    <p className="font-medium truncate">{pdf.name}</p>
                                    <p className="text-xs text-zinc-500">Click to view PDF</p>
                                  </div>
                                  <Download size={18} className="text-zinc-400" />
                                </a>
                              ))}
                            </div>
                          </div>
                        ) : (
                          <p className="text-zinc-500 italic">No result documents available yet.</p>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )
        ) : (
          /* Championship Results Section */
          champResults.length === 0 ? (
            <div className="bg-card border border-zinc-800 p-12 text-center" data-testid="no-champ-results">
              <Trophy className="w-16 h-16 text-zinc-600 mx-auto mb-4" />
              <p className="text-zinc-400 text-xl">No championship standings published yet</p>
              <p className="text-zinc-500 mt-2">Standings will be updated throughout the season!</p>
            </div>
          ) : (
            <div className="space-y-4">
              {champResults.map((result) => {
                const xlsxFiles = result.xlsx_files || [];
                
                return (
                  <div key={result.result_id} className="bg-card border border-zinc-800 overflow-hidden" data-testid={`champ-result-${result.result_id}`}>
                    <div
                      className={`p-6 cursor-pointer hover:bg-zinc-900 transition-colors ${expandedResult === `champ_${result.result_id}` ? 'bg-zinc-900' : ''}`}
                      onClick={() => setExpandedResult(expandedResult === `champ_${result.result_id}` ? null : `champ_${result.result_id}`)}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-4">
                          <div className="w-16 h-16 bg-green-600 flex items-center justify-center">
                            <Trophy size={28} className="text-white" />
                          </div>
                          <div>
                            <h3 className="font-heading font-bold text-xl text-white">{result.title}</h3>
                            <div className="flex items-center space-x-3 text-zinc-400 text-sm mt-1">
                              <span className={`px-2 py-0.5 text-xs font-bold uppercase ${
                                result.league === 'racing' ? 'bg-primary/20 text-primary' : 'bg-secondary/20 text-secondary'
                              }`}>
                                {result.league === 'racing' ? 'Racing' : 'Moto'}
                              </span>
                              {xlsxFiles.length > 0 && (
                                <span className="px-2 py-0.5 text-xs font-bold bg-green-500/20 text-green-500">
                                  {xlsxFiles.length} File{xlsxFiles.length !== 1 ? 's' : ''}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center space-x-4">
                          {expandedResult === `champ_${result.result_id}` ? <ChevronUp size={24} className="text-zinc-400" /> : <ChevronDown size={24} className="text-zinc-400" />}
                        </div>
                      </div>
                    </div>

                    {expandedResult === `champ_${result.result_id}` && (
                      <div className="border-t border-zinc-800 p-6 bg-zinc-950">
                        {result.description && <p className="text-zinc-300 whitespace-pre-wrap mb-6">{result.description}</p>}
                        
                        {xlsxFiles.length > 0 ? (
                          <div>
                            <h4 className="text-sm font-medium text-zinc-500 mb-3">Standings Files:</h4>
                            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                              {xlsxFiles.map((xlsx) => (
                                <div
                                  key={xlsx.url}
                                  className="flex items-center space-x-3 p-4 bg-zinc-900 border border-zinc-800 hover:border-green-500 transition-colors"
                                >
                                  <Table size={24} className="text-green-500 shrink-0" />
                                  <div className="flex-1 min-w-0">
                                    <p className="font-medium truncate">{xlsx.name}</p>
                                    <p className="text-xs text-zinc-500">Spreadsheet</p>
                                  </div>
                                  <div className="flex items-center gap-1.5 shrink-0">
                                    <button
                                      onClick={() => setPreviewXlsx({ url: xlsx.url, name: xlsx.name })}
                                      className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium bg-green-600 hover:bg-green-500 text-white transition-colors"
                                      data-testid={`xlsx-view-${index}`}
                                    >
                                      <Eye size={13} />
                                      View
                                    </button>
                                    <a
                                      href={`${BACKEND_URL}${xlsx.url}`}
                                      download
                                      className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium bg-zinc-700 hover:bg-zinc-600 text-zinc-200 transition-colors"
                                      data-testid={`xlsx-download-${index}`}
                                    >
                                      <Download size={13} />
                                    </a>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        ) : (
                          <p className="text-zinc-500 italic">No standings files available yet.</p>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )
        )}
      </div>

      {/* XLSX Preview Modal */}
      <XlsxPreviewModal
        open={!!previewXlsx}
        onClose={() => setPreviewXlsx(null)}
        xlsxUrl={previewXlsx?.url}
        xlsxName={previewXlsx?.name}
      />
    </div>
    </PageBackground>
  );
}
